<template>
  <button
    class="touch-manipulation rounded-lg p-2.5 text-gray-600 transition-colors hover:bg-gray-100 active:scale-95 sm:hidden dark:text-gray-300 dark:hover:bg-gray-700"
    aria-label="Open search"
    @click="openModal"
  >
    <i class="pi pi-search text-lg"></i>
  </button>

  <div class="relative hidden sm:block">
    <button
      class="group relative flex max-w-[280px] min-w-[200px] cursor-pointer items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm transition-all duration-200 hover:border-purple-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-purple-700"
      aria-label="Open search"
      @click="openModal"
    >
      <i
        class="pi pi-search mr-2 text-sm text-gray-400 transition-colors group-hover:text-purple-500 dark:text-gray-500 dark:group-hover:text-purple-400"
      ></i>
      <span
        class="flex-1 truncate text-left"
        :class="searchQuery ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'"
      >
        {{ searchQuery || $t('search.placeholder') }}
      </span>
      <kbd
        class="ml-2 inline-flex items-center rounded border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400"
      >
        ⌘K
      </kbd>
    </button>
  </div>

  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 backdrop-blur-sm sm:p-6 md:p-20"
      @click.self="closeModal"
    >
      <transition
        enter-active-class="transition ease-out duration-300"
        enter-from-class="opacity-0 scale-95 translate-y-4"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition ease-in duration-200"
        leave-from-class="opacity-100 scale-100 translate-y-0"
        leave-to-class="opacity-0 scale-95 translate-y-4"
      >
        <div
          v-if="isModalOpen"
          class="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl sm:rounded-2xl dark:bg-gray-800"
        >
          <div class="border-b border-gray-200 p-4 sm:p-6 dark:border-gray-700">
            <div class="relative flex items-center">
              <i class="pi pi-search absolute left-4 text-lg text-gray-400 dark:text-gray-500"></i>
              <input
                ref="searchInput"
                v-model="searchQuery"
                type="text"
                :placeholder="$t('search.placeholder')"
                class="w-full rounded-lg border-0 bg-gray-50 py-3 pr-12 pl-12 text-base text-gray-900 placeholder-gray-400 transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:outline-none dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 dark:focus:ring-purple-600"
                @input="debouncedSearch"
                @keydown.esc="closeModal"
                @keydown.down.prevent="navigateDown"
                @keydown.up.prevent="navigateUp"
                @keydown.enter.prevent="selectHighlighted"
              />
              <button
                v-if="searchQuery"
                class="absolute right-3 touch-manipulation p-1.5 text-gray-400 transition-colors hover:text-gray-600 active:scale-95 dark:hover:text-gray-300"
                @click="clearSearch"
              >
                <i class="pi pi-times text-base"></i>
              </button>
              <button
                v-else
                class="absolute right-3 touch-manipulation p-1.5 text-gray-400 transition-colors hover:text-gray-600 active:scale-95 dark:hover:text-gray-300"
                @click="closeModal"
              >
                <i class="pi pi-times text-base"></i>
              </button>
            </div>
          </div>

          <div class="max-h-[60vh] overflow-hidden sm:max-h-[32rem]">
            <div v-if="loading" class="flex flex-col items-center justify-center p-6 sm:p-8">
              <div class="relative">
                <div
                  class="h-10 w-10 rounded-full border-4 border-purple-200 sm:h-12 sm:w-12 dark:border-purple-900"
                ></div>
                <div
                  class="absolute top-0 left-0 h-10 w-10 animate-spin rounded-full border-4 border-purple-600 border-t-transparent sm:h-12 sm:w-12 dark:border-purple-500"
                ></div>
              </div>
              <span class="mt-3 text-sm font-medium text-gray-600 sm:mt-4 dark:text-gray-400">
                {{ $t('search.searching') }}
              </span>
            </div>

            <div
              v-else-if="!loading && results.total === 0"
              class="flex flex-col items-center justify-center p-8 sm:p-12"
            >
              <div
                class="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 sm:mb-4 sm:h-16 sm:w-16 dark:bg-gray-700"
              >
                <i class="pi pi-search text-xl text-gray-400 sm:text-2xl dark:text-gray-500"></i>
              </div>
              <p class="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">{{ $t('search.no_results') }}</p>
              <p class="px-4 text-center text-xs text-gray-500 dark:text-gray-400">
                Try searching for brands, employees, or games
              </p>
            </div>

            <div
              v-else
              class="max-h-[calc(70vh-4rem)] overflow-y-auto overscroll-contain scroll-smooth sm:max-h-[28rem]"
            >
              <SearchResultCategory
                :items="results.results.integration_brands"
                :title="$t('search.integration_brands')"
                icon="tag"
                color="purple"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_brands"
                  :key="item.id"
                  :title="item.name || item.id"
                  icon="tag"
                  color="purple"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_brands', index)"
                  :to="resultRoute('integration_brands', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_brands', index)"
                >
                  <template #metadata>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-building text-[10px]"></i>
                      <span class="max-w-[120px] truncate font-medium sm:max-w-none">{{ item.meta.company }}</span>
                    </div>
                    <BrandEnvironmentTag
                      v-if="item.meta.environment"
                      :environment="item.meta.environment as BrandEnvironment"
                    />
                    <span
                      class="rounded-md bg-green-100 px-2 py-1 text-[10px] font-medium text-green-700 sm:text-xs dark:bg-green-900/30 dark:text-green-300"
                    >
                      {{ item.meta.status }}
                    </span>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.backoffice_companies"
                :title="$t('search.backoffice_companies')"
                icon="building"
                color="blue"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.backoffice_companies"
                  :key="item.id"
                  :title="item.name || item.id"
                  icon="building"
                  color="blue"
                  :is-highlighted="highlightedIndex === getItemIndex('backoffice_companies', index)"
                  :to="resultRoute('backoffice_companies', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('backoffice_companies', index)"
                >
                  <template #metadata>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-tag text-[10px]"></i>
                      <span>{{ item.meta.integration_brands_count }} brands</span>
                    </div>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-users text-[10px]"></i>
                      <span>{{ item.meta.backoffice_customers_count }} customers</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.backoffice_employees"
                :title="$t('search.backoffice_employees')"
                icon="shield"
                color="indigo"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.backoffice_employees"
                  :key="item.id"
                  :title="item.name || item.id"
                  :icon="item.meta.is_super_employee ? 'shield' : 'user'"
                  color="indigo"
                  :is-highlighted="highlightedIndex === getItemIndex('backoffice_employees', index)"
                  :to="resultRoute('backoffice_employees', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('backoffice_employees', index)"
                >
                  <template #metadata>
                    <span
                      v-if="item.meta.is_super_employee"
                      class="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700 uppercase dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      Super
                    </span>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-envelope text-[10px]"></i>
                      <span class="max-w-[150px] truncate sm:max-w-none">{{ item.meta.email }}</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.backoffice_customers"
                :title="$t('search.backoffice_customers')"
                icon="users"
                color="green"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.backoffice_customers"
                  :key="item.id"
                  :title="item.name || item.id"
                  icon="user"
                  color="green"
                  :is-highlighted="highlightedIndex === getItemIndex('backoffice_customers', index)"
                  :to="resultRoute('backoffice_customers', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('backoffice_customers', index)"
                >
                  <template #metadata>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-envelope text-[10px]"></i>
                      <span class="max-w-[150px] truncate sm:max-w-none">{{ item.meta.email }}</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.integration_main_providers"
                :title="$t('search.integration_main_providers')"
                icon="sitemap"
                color="orange"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_main_providers"
                  :key="item.id"
                  :title="item.name || item.id"
                  icon="sitemap"
                  color="orange"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_main_providers', index)"
                  :to="resultRoute('integration_main_providers', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_main_providers', index)"
                >
                  <template #metadata>
                    <span
                      class="rounded-md bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-700 sm:text-xs dark:bg-gray-800/80 dark:text-gray-300"
                    >
                      {{ item.meta.type }}
                    </span>
                    <span
                      class="rounded-md bg-green-100 px-2 py-1 text-[10px] font-medium text-green-700 sm:text-xs dark:bg-green-900/30 dark:text-green-300"
                    >
                      {{ item.meta.status }}
                    </span>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.integration_providers"
                :title="$t('search.integration_providers')"
                icon="server"
                color="cyan"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_providers"
                  :key="item.id"
                  :title="item.name || item.id"
                  icon="server"
                  color="cyan"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_providers', index)"
                  :to="resultRoute('integration_providers', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_providers', index)"
                >
                  <template #metadata>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <span class="truncate">{{ item.meta.main_provider }}</span>
                    </div>
                    <span
                      v-if="item.meta.on_maintenance"
                      class="flex items-center gap-1 rounded-md bg-orange-100 px-2 py-1 text-[10px] font-medium text-orange-700 sm:text-xs dark:bg-orange-900/30 dark:text-orange-300"
                    >
                      <i class="pi pi-wrench text-[10px]"></i>
                      {{ $t('search.maintenance') }}
                    </span>
                    <span
                      v-else-if="!item.meta.active"
                      class="flex items-center gap-1 rounded-md bg-red-100 px-2 py-1 text-[10px] font-medium text-red-700 sm:text-xs dark:bg-red-900/30 dark:text-red-300"
                    >
                      <i class="pi pi-times-circle text-[10px]"></i>
                      {{ $t('search.inactive') }}
                    </span>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.integration_games"
                :title="$t('search.integration_games')"
                icon="box"
                color="pink"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_games"
                  :key="item.id"
                  :title="item.identifier ? `${item.name || item.id} (${item.identifier})` : item.name || item.id"
                  icon="box"
                  color="pink"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_games', index)"
                  :to="resultRoute('integration_games', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_games', index)"
                >
                  <template #metadata>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <span class="truncate">{{ item.meta.provider }}</span>
                    </div>
                    <span class="text-gray-400 dark:text-gray-600">•</span>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <span class="truncate">{{ item.meta.main_provider }}</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.integration_players"
                :title="$t('search.integration_players')"
                icon="users"
                color="teal"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_players"
                  :key="item.id"
                  :title="(item.name as string) || (item.username as string) || item.id"
                  icon="user"
                  color="teal"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_players', index)"
                  :to="resultRoute('integration_players', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_players', index)"
                >
                  <template #subtitle>
                    <div class="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-hashtag text-[8px]"></i>
                        <span class="font-mono">{{ item.player_id }}</span>
                      </div>
                      <span v-if="item.email" class="text-gray-400">•</span>
                      <div v-if="item.email" class="flex items-center gap-1">
                        <i class="pi pi-envelope text-[8px]"></i>
                        <span class="truncate">{{ item.email }}</span>
                      </div>
                    </div>
                  </template>
                  <template #metadata>
                    <div
                      v-if="item.meta.brand_name"
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <i class="pi pi-tag text-[10px]"></i>
                      <span class="truncate">{{ item.meta.brand_name }}</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>

              <SearchResultCategory
                :items="results.results.integration_transactions"
                :title="$t('search.integration_transactions')"
                icon="dollar"
                color="emerald"
              >
                <SearchResultItem
                  v-for="(item, index) in results.results.integration_transactions"
                  :key="item.id"
                  :title="item.id"
                  icon="dollar"
                  color="emerald"
                  :is-highlighted="highlightedIndex === getItemIndex('integration_transactions', index)"
                  :to="resultRoute('integration_transactions', item.id)"
                  @click="closeUnlessOpeningElsewhere"
                  @mouseenter="highlightedIndex = getItemIndex('integration_transactions', index)"
                >
                  <template #metadata>
                    <span
                      class="rounded-md px-2 py-1 text-[10px] font-medium sm:text-xs"
                      :class="{
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300':
                          item.meta.status === 'completed',
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': item.meta.status === 'failed',
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300':
                          item.meta.status === 'pending',
                      }"
                    >
                      {{ item.meta.status }}
                    </span>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <span class="truncate">{{ item.meta.transaction_type }}</span>
                    </div>
                    <div
                      class="flex items-center gap-1.5 rounded-md bg-gray-100/80 px-2 py-1 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-400"
                    >
                      <span>{{ item.meta.amount }} {{ item.meta.currency }}</span>
                    </div>
                    <div
                      v-if="item.meta.external_id"
                      v-tooltip.top="`External ID: ${item.meta.external_id}`"
                      class="flex items-center gap-1 rounded-md bg-blue-100/80 px-2 py-1 font-mono text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      <i class="pi pi-link text-[8px]"></i>
                      <span>{{ String(item.meta.external_id).substring(0, 8) }}...</span>
                    </div>
                    <div
                      v-if="item.meta.correlation_id"
                      v-tooltip.top="`Correlation ID: ${item.meta.correlation_id}`"
                      class="flex items-center gap-1 rounded-md bg-purple-100/80 px-2 py-1 font-mono text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                    >
                      <i class="pi pi-sitemap text-[8px]"></i>
                      <span>{{ String(item.meta.correlation_id).substring(0, 8) }}...</span>
                    </div>
                  </template>
                </SearchResultItem>
              </SearchResultCategory>
            </div>

            <div
              v-if="!loading && results.total > 0"
              class="border-t border-gray-200 bg-gray-50 px-3 py-2.5 text-center sm:px-4 sm:py-3 dark:border-gray-700 dark:bg-gray-900"
            >
              <p class="text-xs font-medium text-gray-600 dark:text-gray-400">
                {{ $t('search.total_results', { count: results.total }) }}
              </p>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { debounce } from 'lodash-es';

import { Routes } from '@/router/routes';
import { navigateTo, routeLocation } from '@/router/typesafe';
import { API_ROUTES, apiRequest } from '@/types/api-routes';
import type { BrandEnvironment } from '@/types/enums';
import { opensOutsideCurrentTab } from '@/utils/link';

import BrandEnvironmentTag from './Brands/BrandEnvironmentTag.vue';
import SearchResultCategory from './Search/SearchResultCategory.vue';
import SearchResultItem from './Search/SearchResultItem.vue';

interface SearchResult {
  type: string;
  id: string;
  name?: string;
  identifier?: string;
  player_id?: string;
  username?: string;
  email?: string;
  meta: Record<string, unknown>;
}

interface SearchResults {
  query: string;
  results: {
    integration_brands: SearchResult[];
    backoffice_companies: SearchResult[];
    backoffice_employees: SearchResult[];
    backoffice_customers: SearchResult[];
    integration_main_providers: SearchResult[];
    integration_providers: SearchResult[];
    integration_games: SearchResult[];
    integration_players: SearchResult[];
    integration_transactions: SearchResult[];
  };
  total: number;
}

const RESULT_ROUTES: Record<string, typeof Routes.Admin.Brands.Show> = {
  integration_brands: Routes.Admin.Brands.Show,
  backoffice_companies: Routes.Admin.Companies.Show,
  backoffice_employees: Routes.Admin.Employees.Users.Show,
  backoffice_customers: Routes.Admin.Customers.Show,
  integration_main_providers: Routes.Admin.MainProviders.Show,
  integration_providers: Routes.Admin.Providers.Show,
  integration_games: Routes.Admin.Games.Show,
  integration_players: Routes.Admin.Players.Show,
  integration_transactions: Routes.Admin.Transactions.Details,
};

const resultParams = (type: string, id: string): Record<string, string> =>
  type === 'integration_transactions' ? { transactionId: id } : { id };

const searchQuery = ref('');
const isModalOpen = ref(false);
const loading = ref(false);
const highlightedIndex = ref(-1);
const searchInput = ref<HTMLInputElement | null>(null);
const results = ref<SearchResults>({
  query: '',
  results: {
    integration_brands: [],
    backoffice_companies: [],
    backoffice_employees: [],
    backoffice_customers: [],
    integration_main_providers: [],
    integration_providers: [],
    integration_games: [],
    integration_players: [],
    integration_transactions: [],
  },
  total: 0,
});

const flatResults = computed(() => {
  const flat: Array<{ type: string; item: SearchResult; index: number }> = [];
  let currentIndex = 0;

  Object.entries(results.value.results).forEach(([type, items]) => {
    items.forEach(item => {
      flat.push({ type, item, index: currentIndex++ });
    });
  });

  return flat;
});

const performSearch = async () => {
  if (!searchQuery.value.trim()) {
    results.value = {
      query: '',
      results: {
        integration_brands: [],
        backoffice_companies: [],
        backoffice_employees: [],
        backoffice_customers: [],
        integration_main_providers: [],
        integration_providers: [],
        integration_games: [],
        integration_players: [],
        integration_transactions: [],
      },
      total: 0,
    };
    return;
  }

  loading.value = true;

  try {
    const result = await apiRequest<SearchResults>(API_ROUTES.admin.search, {
      query: searchQuery.value,
      limit: 10,
      exact_match: true,
    });

    if (result.ok) {
      results.value = result.data;
    } else {
      console.error('Search error:', result.error);
    }
  } catch (error) {
    console.error('Search error:', error);
  } finally {
    loading.value = false;
  }
};

const debouncedSearch = debounce(performSearch, 300);

const getItemIndex = (type: string, itemIndex: number): number => {
  let offset = 0;
  const types = [
    'integration_brands',
    'backoffice_companies',
    'backoffice_employees',
    'backoffice_customers',
    'integration_main_providers',
    'integration_providers',
    'integration_games',
    'integration_players',
    'integration_transactions',
  ];

  for (const t of types) {
    if (t === type) {
      return offset + itemIndex;
    }
    const length = results.value.results[t as keyof typeof results.value.results].length;
    offset += length;
  }

  return -1;
};

const navigateDown = () => {
  if (flatResults.value.length === 0) {
    return;
  }
  highlightedIndex.value = Math.min(highlightedIndex.value + 1, flatResults.value.length - 1);
};

const navigateUp = () => {
  highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
};

const selectHighlighted = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < flatResults.value.length) {
    const item = flatResults.value[highlightedIndex.value];
    navigateTo(RESULT_ROUTES[item.type], resultParams(item.type, item.item.id));
    closeModal();
  }
};

const resultRoute = (type: string, id: string): RouteLocationRaw =>
  routeLocation(RESULT_ROUTES[type], resultParams(type, id));

const closeUnlessOpeningElsewhere = (event: MouseEvent) => {
  if (!opensOutsideCurrentTab(event)) {
    closeModal();
  }
};

const openModal = () => {
  isModalOpen.value = true;

  setTimeout(() => {
    searchInput.value?.focus();

    if (searchQuery.value.trim()) {
      performSearch();
    }
  }, 100);
};

const closeModal = () => {
  isModalOpen.value = false;
  highlightedIndex.value = -1;
};

const clearSearch = () => {
  searchQuery.value = '';
  highlightedIndex.value = -1;
  results.value = {
    query: '',
    results: {
      integration_brands: [],
      backoffice_companies: [],
      backoffice_employees: [],
      backoffice_customers: [],
      integration_main_providers: [],
      integration_providers: [],
      integration_games: [],
      integration_players: [],
      integration_transactions: [],
    },
    total: 0,
  };

  setTimeout(() => {
    searchInput.value?.focus();
  }, 0);
};

watch(isModalOpen, newValue => {
  document.body.style.overflow = newValue ? 'hidden' : '';
});

onMounted(() => {
  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      openModal();
    }

    if (e.key === 'Escape' && isModalOpen.value) {
      closeModal();
    }
  };

  window.addEventListener('keydown', handleKeydown);
  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);

    document.body.style.overflow = '';
  });
});
</script>
